/* eslint-disable max-len */
// Per-page, per-machine persistence of child-window size + position for ATLAS
// and WATCHLIST. "System specific" per the requirement → stored in localStorage
// (device/browser-local, never synced to the account). Geometry only applies to
// popup windows the app opens via window.open; browsers ignore size/position on
// the main tab. See docs/requirements/irs-monitor/window-size-persisance.

const STORAGE_PREFIX = 'deon:winGeom:';

const hasWindow = () => typeof window !== 'undefined';

const storageKey = (pageKey) => `${STORAGE_PREFIX}${pageKey}`;

const isFiniteNumber = (n) => typeof n === 'number' && Number.isFinite(n);

// A valid geometry has positive width/height and finite left/top.
const isValidGeometry = (geom) => Boolean(geom)
    && isFiniteNumber(geom.width) && geom.width > 0
    && isFiniteNumber(geom.height) && geom.height > 0
    && isFiniteNumber(geom.left)
    && isFiniteNumber(geom.top);

// Reads the live outer size + on-screen position of the current window.
export const getCurrentWindowGeometry = () => {
    if (!hasWindow()) return null;
    return {
        width: window.outerWidth,
        height: window.outerHeight,
        left: window.screenX,
        top: window.screenY
    };
};

export const getWindowGeometry = (pageKey) => {
    if (!hasWindow() || !pageKey) return null;
    try {
        const raw = localStorage.getItem(storageKey(pageKey));
        if (!raw) return null;
        const geom = JSON.parse(raw);
        return isValidGeometry(geom) ? geom : null;
    } catch (e) {
        // Corrupt / unreadable storage → behave as if nothing was saved.
        return null;
    }
};

export const saveWindowGeometry = (pageKey, geom) => {
    if (!hasWindow() || !pageKey || !isValidGeometry(geom)) return;
    try {
        localStorage.setItem(storageKey(pageKey), JSON.stringify({
            width: Math.round(geom.width),
            height: Math.round(geom.height),
            left: Math.round(geom.left),
            top: Math.round(geom.top)
        }));
    } catch (e) {
        // Storage quota / privacy mode — ignore, geometry just won't persist.
    }
};

// Keeps a restored window fully within the current screen. Guards against a
// layout saved on a larger or second monitor that no longer exists.
export const clampToScreen = (geom) => {
    if (!isValidGeometry(geom)) return geom;
    const screen = (hasWindow() && window.screen) ? window.screen : {};
    const availWidth = isFiniteNumber(screen.availWidth) ? screen.availWidth : geom.width;
    const availHeight = isFiniteNumber(screen.availHeight) ? screen.availHeight : geom.height;
    const availLeft = isFiniteNumber(screen.availLeft) ? screen.availLeft : 0;
    const availTop = isFiniteNumber(screen.availTop) ? screen.availTop : 0;

    const width = Math.min(geom.width, availWidth);
    const height = Math.min(geom.height, availHeight);
    const maxLeft = availLeft + availWidth - width;
    const maxTop = availTop + availHeight - height;
    const left = Math.min(Math.max(geom.left, availLeft), maxLeft);
    const top = Math.min(Math.max(geom.top, availTop), maxTop);

    return {
        width, height, left, top
    };
};

// Builds a window.open features string from the saved geometry, or returns the
// caller's default features when nothing (valid) is saved.
export const buildPopupFeatures = (pageKey, defaultFeatures) => {
    const geom = getWindowGeometry(pageKey);
    if (!geom) return defaultFeatures;
    const {
        width, height, left, top
    } = clampToScreen(geom);
    return `width=${width},height=${height},left=${left},top=${top},popup`;
};

// Force size/position on a window we just opened. window.open features are
// ignored when a named window is reused, and unreliable when the opener is
// already a popup (e.g. ATLAS from WATCHLIST). resizeTo/moveTo still work on
// the returned handle when called in the same user gesture.
const applySavedGeometry = (targetWindow, pageKey) => {
    if (!targetWindow || targetWindow.closed) return;
    const geom = getWindowGeometry(pageKey);
    if (!geom) return;
    const {
        width, height, left, top
    } = clampToScreen(geom);
    try {
        targetWindow.resizeTo(Math.round(width), Math.round(height));
        targetWindow.moveTo(Math.round(left), Math.round(top));
    } catch (e) {
        // Browser blocked resize/move — features string above is the fallback.
    }
};

// Single entry point for opening ATLAS / WATCHLIST. Fixed window name (pageKey)
// so re-opening reuses/refocuses the same window instead of spawning duplicates.
export const openTrackedWindow = (pageKey, url, defaultFeatures) => {
    if (!hasWindow()) return null;
    const win = window.open(url, pageKey, buildPopupFeatures(pageKey, defaultFeatures));
    if (win) applySavedGeometry(win, pageKey);
    return win;
};
